import React, { useLayoutEffect, useRef, useState } from 'react';

interface RoomSelectorProps {
  selectedRoom: string;
  onRoomChange: (room: string) => void;
  rooms: string[];
  showUnassigned: boolean;
}

const RoomSelector: React.FC<RoomSelectorProps> = ({ 
  selectedRoom, 
  onRoomChange,
  rooms,
  showUnassigned
}) => {
  const tabsRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0 });

  const updateIndicatorPosition = () => {
    const activeTabKey = selectedRoom === 'all' 
      ? 'all' 
      : selectedRoom === 'unassigned' 
      ? 'unassigned' 
      : selectedRoom;
    
    const activeTab = tabRefs.current.get(activeTabKey);
    const tabsContainer = tabsRef.current;

    if (activeTab && tabsContainer) {
      const tabsContainerRect = tabsContainer.getBoundingClientRect();
      const activeTabRect = activeTab.getBoundingClientRect();
      
      const left = activeTabRect.left - tabsContainerRect.left;
      
      setIndicatorStyle({
        left: left,
        width: activeTabRect.width
      });
    }
  };

  useLayoutEffect(() => {
    const tabsContainer = tabsRef.current;
    if (tabsContainer) {
      const updateWithDelay = () => {
        requestAnimationFrame(() => {
          requestAnimationFrame(updateIndicatorPosition);
        });
      };
      
      updateWithDelay();
      
      tabsContainer.addEventListener('scroll', updateIndicatorPosition, { passive: true });
      window.addEventListener('resize', updateIndicatorPosition);
      
      return () => {
        tabsContainer.removeEventListener('scroll', updateIndicatorPosition);
        window.removeEventListener('resize', updateIndicatorPosition);
      };
    }
  }, [selectedRoom, rooms, showUnassigned]);

  const setTabRef = (key: string, element: HTMLButtonElement | null) => {
    if (element) {
      tabRefs.current.set(key, element);
    } else {
      tabRefs.current.delete(key);
    }
  };

  return (
    <div className="room-selector">
      <div className="room-tabs" ref={tabsRef}>
        <div 
          className="room-tab-indicator"
          style={{
            left: `${indicatorStyle.left}px`,
            width: `${indicatorStyle.width}px`
          }}
        />
        <button
          ref={(el) => setTabRef('all', el)}
          className={`room-tab ${selectedRoom === 'all' ? 'active' : ''}`}
          onClick={() => onRoomChange('all')}
        >
          Hammasi
        </button>
        {showUnassigned && (
          <button
            ref={(el) => setTabRef('unassigned', el)}
            className={`room-tab ${selectedRoom === 'unassigned' ? 'active' : ''}`}
            onClick={() => onRoomChange('unassigned')}
          >
            Belgilanmagan
          </button>
        )}
        {rooms.map((room) => (
          <button
            key={room}
            ref={(el) => setTabRef(room, el)}
            className={`room-tab ${selectedRoom === room ? 'active' : ''}`}
            onClick={() => onRoomChange(room)}
          >
            {room}
          </button>
        ))}
      </div>
    </div>
  );
};

export default RoomSelector;

